import React, { useEffect, useMemo, useState } from "react";
import Translations from "../../services/translation.service";
import Storage from "../../services/storage.service";
import storefront from "../../services/storefront.service";
import Helpers from "../../helpers/shared.helper";
import DataIcon from "../../assets/img/stm-data.svg";
import { resolveSocialIconUrl } from "../../helpers/egyptFlexSocial.helper";

const prefix_general = "stm_general_";

const formatUnlimited = (translationKeys) =>
  translationKeys["free_addons_unlimited"] ||
  translationKeys["egypt_flex_roaming_unlimited_short"] ||
  "Unlimited";

const formatDataValue = (plan, translationKeys) => {
  if (!plan) {
    return "";
  }
  if (plan.Value === -1 || Helpers.isUnlimitedPlanValue(plan.Value)) {
    return formatUnlimited(translationKeys);
  }
  const value = storefront.getTransformedPlanTypeValues(plan.Value, plan.Unit);
  if (value === -1) {
    return formatUnlimited(translationKeys);
  }
  const unit = translationKeys[`${prefix_general}data_unit`] || "GB";
  return `${value} ${unit}`;
};

const formatMinutesValue = (plan, translationKeys) => {
  if (!plan) {
    return "";
  }
  if (plan.Value === -1 || Helpers.isUnlimitedPlanValue(plan.Value)) {
    return formatUnlimited(translationKeys);
  }
  const value = storefront.getTransformedPlanTypeValues(plan.Value, plan.Unit);
  if (value === -1) {
    return formatUnlimited(translationKeys);
  }
  const unit =
    translationKeys[`${prefix_general}minutes_unit`] ||
    translationKeys[`${prefix_general}minute_unit`] ||
    "min";
  return `${value} ${unit}`;
};

const SummaryRow = ({ label, value }) => {
  if (!value) {
    return null;
  }

  return (
    <div className="egypt-flex-summary-row">
      <span className="egypt-flex-summary-row-label">{label}</span>
      <span className="egypt-flex-summary-row-value">{value}</span>
    </div>
  );
};

const EgyptFlexSummaryPlan = ({ plan = {}, price }) => {
  const language = Translations.getCurrentLanguage();
  const [themeIconsRevision, setThemeIconsRevision] = useState(0);
  const [translationKeys, setTranslationKeys] = useState(
    Translations.keys ? Translations.keys : {}
  );

  useEffect(() => {
    const subscriptions = [
      Translations.getKeysObsrv().subscribe((data) => {
        setTranslationKeys(data);
      }),
      Storage.get_obsrv("themeLoading").subscribe((loading) => {
        if (loading === false || loading === "false") {
          setThemeIconsRevision((revision) => revision + 1);
        }
      }),
      Storage.get_obsrv("social_networks").subscribe(() => {
        setThemeIconsRevision((revision) => revision + 1);
      }),
    ];

    return () => {
      subscriptions.forEach((subscription) => subscription.unsubscribe());
    };
  }, []);

  const dataPlan = Helpers.getPlanSpec(plan, "DATA");
  const flexiPlan = Helpers.getPlanSpec(plan, "VOICE-FLEXI");
  const roamingPlan = Helpers.getPlanSpec(plan, "VOICE-IN-ROM");
  const socialPlan = Helpers.getPlanSpec(plan, "DATA-SOCIAL");

  const processedPlanCode = Helpers.processPlanCode(plan?.PlanCode);
  const planTitle =
    translationKeys[`general_plan_bundle_name_${processedPlanCode}`] ||
    translationKeys[`general_plan_name_${processedPlanCode}`] ||
    plan?.PlanCode;

  const displayPrice =
    price ??
    plan?.PlanCardPrice ??
    plan?.DiscountedPrice ??
    plan?.MonthlyDiscountedPrice;

  const socialChannels = useMemo(
    () => Helpers.getSocialChannels(plan),
    [plan]
  );

  const socialIcons = useMemo(
    () =>
      socialChannels
        .map((channel) => ({
          code: channel.Code,
          url: resolveSocialIconUrl(channel.Code, language),
        }))
        .filter((item) => item.url),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [socialChannels, language, themeIconsRevision]
  );

  const rows = useMemo(() => {
    const items = [];

    if (dataPlan) {
      items.push({
        key: "core-data",
        label:
          translationKeys[`${prefix_general}data`] ||
          translationKeys["egypt_flex_summary_core_data"] ||
          "Core data",
        value: formatDataValue(dataPlan, translationKeys),
      });
    }

    if (flexiPlan) {
      items.push({
        key: "flex-minutes",
        label:
          translationKeys["sf_plan_detail_plan_type_voice_flexi"] ||
          translationKeys["egypt_flex_flex_min"] ||
          "Flex minutes",
        value: formatMinutesValue(flexiPlan, translationKeys),
      });
    }

    if (roamingPlan) {
      items.push({
        key: "roaming-minutes",
        label: translationKeys["egypt_flex_summary_roaming_minutes"] || "Roaming minutes",
        value: formatUnlimited(translationKeys),
      });
    }

    if (socialPlan) {
      items.push({
        key: "social-data",
        label: translationKeys["egypt_flex_social_data"] || "Social data",
        value: formatUnlimited(translationKeys),
      });
    }

    return items;
  }, [dataPlan, flexiPlan, roamingPlan, socialPlan, translationKeys]);

  if (!Helpers.isEgyptFlexPlan(plan)) {
    return null;
  }

  return (
    <div className="egypt-flex-summary-plan">
      <div className="egypt-flex-summary-plan-header">
        <img src={DataIcon} alt="" className="egypt-flex-summary-plan-icon" />
        <span className="egypt-flex-summary-plan-title">
          {Helpers.nextLineText(planTitle, "span")}
        </span>
        {displayPrice != null && displayPrice !== "" && (
          <span className="egypt-flex-summary-plan-price">
            {displayPrice}{" "}
            {translationKeys[`${prefix_general}currency`]}
          </span>
        )}
      </div>

      {rows.length > 0 && (
        <div className="egypt-flex-summary-plan-rows">
          {rows.map((row) => (
            <SummaryRow key={row.key} label={row.label} value={row.value} />
          ))}
        </div>
      )}

      {socialIcons.length > 0 && (
        <div className="egypt-flex-summary-social-icons">
          {socialIcons.map((item) => (
            <img key={item.code} src={item.url} alt={item.code} />
          ))}
        </div>
      )}
    </div>
  );
};

export default EgyptFlexSummaryPlan;
